import { useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { L, Icon } from '../ui/LavDropdown'
import { MbIc, ApdPill, MobileSheet, SheetCancelButton, MbPrimaryButton } from './mbBits'
import {
	AppointmentSections,
	computeApptDisplay,
	DANGER,
	DANGER_SOFT,
	WARN_IC,
	WARN_BG,
} from './apdShared'

dayjs.extend(utc)
dayjs.extend(timezone)

/* ── ghost footer button ── */
function GhostButton({ onClick, children, ariaLabel, square = false, disabled = false, sx = {} }) {
	return (
		<Box
			component="button"
			type="button"
			aria-label={ariaLabel}
			onClick={onClick}
			disabled={disabled}
			sx={{
				flex: 'none', height: 52, width: square ? 52 : 'auto', px: square ? 0 : '18px',
				borderRadius: '14px', border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF',
				color: square ? L.muted : L.text, fontSize: '15px', fontWeight: 700,
				display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
				fontFamily: 'inherit', cursor: 'pointer',
				'&:active:not(:disabled)': { bgcolor: L.surface, borderColor: L.muted },
				'&:disabled': { opacity: 0.6, cursor: 'default' },
				'& svg': { color: L.muted, flex: 'none' },
				...sx,
			}}
		>
			{children}
		</Box>
	)
}

/**
 * MobileAppointmentDetails — full-screen mobile view of a booked appointment,
 * per the Claude Design "Mobile · appointment details" artboards: status hero,
 * banner with Undo, When card, pet cards, notes, total, and a sticky footer
 * (Edit / ⋯ action sheet / Checkout). Purely presentational — all mutations
 * come in as callbacks from AppointmentDetailsDialog.
 */
export default function MobileAppointmentDetails({
	appointment,
	client,
	pet,
	service,
	groupedAppointments = [],
	merchant,
	status,
	paidRecord,
	loading,
	updatingStatus,
	formatCurrency,
	withinCancelWindow = false,
	// False when the appointment has no Stripe customer / payment method on
	// file (merchant-created bookings): no fee can be charged, so the no-show
	// confirm doesn't mention one.
	canChargeFee = false,
	onClose,
	onEdit,
	enteringEdit = false,
	// Reschedule fast path (design "Reschedule · mobile"): the When card row
	// and the manage sheet both open the full-screen slot picker in the host.
	onReschedule,
	moved = null,
	onCheckout,
	onUndo,
	onNoShow,
	onCancelNoFee,
	onCancelWithFee,
	onOpenClientProfile,
	onOpenPetProfile,
	// Completed only — "More actions" opens the invoice sheet (design
	// MbapdInvoiceSheet): View invoice / Download PDF.
	onViewInvoice,
	onDownloadInvoice,
	downloadingInvoice = false,
}) {
	const [sheetOpen, setSheetOpen] = useState(false)
	const [invSheetOpen, setInvSheetOpen] = useState(false)
	const [confirm, setConfirm] = useState(null) // 'noshow' | 'cancel' | null

	const tz = merchant?.timezone || 'UTC'

	// Normalize single vs grouped appointments into display entries + totals.
	const display = computeApptDisplay({ appointment, client, pet, service, groupedAppointments, tz })
	const { start, totalPrice, petNames, whenShort } = display

	const dimmed = status === 'noShow' || status === 'cancelled'
	const isActionable = status === 'booked' || status === 'bookPending'
	// Once paid, no-show / cancel no longer apply; a single More actions
	// button holds the invoice actions instead.
	const invoiceActions = status === 'completed' && Boolean(paidRecord)
		&& Boolean(onViewInvoice || onDownloadInvoice)

	const requestStatus = (kind) => {
		setSheetOpen(false)
		setConfirm(kind)
	}

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: L.bg, color: L.text, fontFamily: L.font, position: 'relative',
		}}>
			{/* ── hero ── */}
			<Box sx={{ flex: 'none', p: 'calc(env(safe-area-inset-top, 0px) + 16px) 16px 10px' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Box
						component="button"
						type="button"
						aria-label="Close"
						onClick={onClose}
						sx={{
							width: 40, height: 40, flex: 'none', ml: '-6px', border: 'none', borderRadius: '12px',
							bgcolor: 'transparent', color: L.text, display: 'flex', alignItems: 'center',
							justifyContent: 'center', cursor: 'pointer', '&:active': { bgcolor: L.surface },
						}}
					>
						<MbIc name="back" size={18} />
					</Box>
					<Box component="h1" sx={{
						m: 0, fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em',
						whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
					}}>
						Appointment
					</Box>
					<ApdPill status={status} mobile />
				</Box>
				<Box component="p" sx={{ m: '4px 0 0 34px', fontSize: '13.5px', fontWeight: 600, color: L.muted }}>
					{petNames && `${petNames} · `}{start.format('ddd, MMM D')} · {start.format('h:mm A')}
				</Box>
			</Box>

			{/* ── body ── */}
			<Box sx={{
				flex: 1, minHeight: 0, overflowY: 'auto', p: '10px 16px 26px',
				display: 'flex', flexDirection: 'column', gap: '20px',
			}}>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress size={24} sx={{ color: L.primary }} />
					</Box>
				) : (
					<AppointmentSections
						appointment={appointment}
						client={client}
						display={display}
						status={status}
						paidRecord={paidRecord}
						tz={tz}
						formatCurrency={formatCurrency}
						onUndo={onUndo}
						updatingStatus={updatingStatus}
						dimmed={dimmed}
						onOpenClientProfile={onOpenClientProfile}
						onOpenPetProfile={onOpenPetProfile}
						showPetCount
						whenAction={isActionable && onReschedule ? { label: 'Reschedule', icon: 'move', onClick: onReschedule } : null}
						moved={moved}
					/>
				)}
			</Box>

			{/* ── footer (booked only) ── */}
			{isActionable && !loading && (
				<Box sx={{
					flex: 'none', display: 'flex', gap: '10px',
					p: '12px 16px calc(env(safe-area-inset-bottom, 0px) + 20px)',
					borderTop: `1px solid ${L.border}`, bgcolor: L.bg,
				}}>
					<GhostButton onClick={onEdit} disabled={enteringEdit}>
						{enteringEdit
							? <CircularProgress size={15} sx={{ color: L.muted }} />
							: <Icon name="pencil" size={15} />}
						<span>Edit</span>
					</GhostButton>
					<GhostButton square ariaLabel="More actions" onClick={() => setSheetOpen(true)}>
						<MbIc name="dots" size={18} />
					</GhostButton>
					<MbPrimaryButton onClick={onCheckout} disabled={updatingStatus}>
						<MbIc name="card" size={16} />
						<span>Checkout · {formatCurrency(totalPrice)}</span>
					</MbPrimaryButton>
				</Box>
			)}

			{/* ── footer (completed only): More actions → invoice sheet ── */}
			{invoiceActions && !loading && (
				<Box sx={{
					flex: 'none', display: 'flex', gap: '10px',
					p: '12px 16px calc(env(safe-area-inset-bottom, 0px) + 20px)',
					borderTop: `1px solid ${L.border}`, bgcolor: L.bg,
				}}>
					<GhostButton
						onClick={() => setInvSheetOpen(true)}
						ariaLabel="More actions"
						sx={{ flex: '1 1 100%', width: '100%' }}
					>
						<MbIc name="dots" size={17} />
						<span>More actions</span>
					</GhostButton>
				</Box>
			)}

			{/* ── invoice sheet (completed · design MbapdInvoiceSheet) ── */}
			<MobileSheet open={invSheetOpen} onClose={() => setInvSheetOpen(false)} title="Invoice">
				{onViewInvoice && (
					<Box
						component="button"
						type="button"
						onClick={() => { setInvSheetOpen(false); onViewInvoice() }}
						sx={invSheetItemSx}
					>
						<Box sx={{ ...invSheetIcSx }}><MbIc name="receipt" size={17} /></Box>
						<Box component="strong" sx={invSheetTxtSx}>View invoice</Box>
					</Box>
				)}
				{onDownloadInvoice && (
					<Box
						component="button"
						type="button"
						disabled={downloadingInvoice}
						onClick={() => { setInvSheetOpen(false); onDownloadInvoice() }}
						sx={invSheetItemSx}
					>
						<Box sx={{ ...invSheetIcSx }}>
							{downloadingInvoice
								? <CircularProgress size={16} sx={{ color: L.primaryDeep }} />
								: <MbIc name="download" size={17} />}
						</Box>
						<Box component="strong" sx={invSheetTxtSx}>
							{downloadingInvoice ? 'Preparing PDF…' : 'Download PDF'}
						</Box>
					</Box>
				)}
				<SheetCancelButton onClick={() => setInvSheetOpen(false)}>Close</SheetCancelButton>
			</MobileSheet>

			{/* ── action sheet ── */}
			<MobileSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="Manage appointment">
				{onReschedule && (
					<Box
						component="button"
						type="button"
						onClick={() => { setSheetOpen(false); onReschedule() }}
						sx={sheetItemSx}
					>
						<Box sx={{ ...sheetIcSx, bgcolor: L.primarySoft, color: L.primaryDeep }}><MbIc name="move" size={17} /></Box>
						<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left' }}>
							<Box component="strong" sx={{ fontSize: '15.5px', fontWeight: 750, color: L.text }}>Reschedule</Box>
							<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted }}>Pick a new slot — services & price stay</Box>
						</Box>
					</Box>
				)}
				<Box
					component="button"
					type="button"
					onClick={() => requestStatus('noshow')}
					sx={sheetItemSx}
				>
					<Box sx={{ ...sheetIcSx, bgcolor: WARN_BG, color: WARN_IC }}><MbIc name="warn" size={17} /></Box>
					<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left' }}>
						<Box component="strong" sx={{ fontSize: '15.5px', fontWeight: 750, color: L.text }}>Mark as no-show</Box>
						<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted }}>Client didn’t arrive for this visit</Box>
					</Box>
				</Box>
				<Box
					component="button"
					type="button"
					onClick={() => requestStatus('cancel')}
					sx={sheetItemSx}
				>
					<Box sx={{ ...sheetIcSx, bgcolor: DANGER_SOFT, color: DANGER }}><MbIc name="ban" size={16} /></Box>
					<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left' }}>
						<Box component="strong" sx={{ fontSize: '15.5px', fontWeight: 750, color: DANGER }}>Cancel appointment</Box>
						<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted }}>Release this time slot</Box>
					</Box>
				</Box>
				<SheetCancelButton onClick={() => setSheetOpen(false)}>Close</SheetCancelButton>
			</MobileSheet>

			{/* ── confirm sheet ── */}
			<MobileSheet open={!!confirm} onClose={() => setConfirm(null)} center>
				<Box sx={{
					width: 52, height: 52, m: '4px auto 14px', borderRadius: '16px',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					bgcolor: confirm === 'cancel' ? DANGER_SOFT : WARN_BG,
					color: confirm === 'cancel' ? DANGER : WARN_IC,
				}}>
					{confirm === 'cancel' ? <MbIc name="ban" size={22} /> : <MbIc name="warn" size={22} />}
				</Box>
				<Box component="strong" sx={{ display: 'block', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.01em', color: L.text }}>
					{confirm === 'cancel' ? 'Cancel this appointment?' : 'Mark as no-show?'}
				</Box>
				<Box component="p" sx={{ m: '8px 0 0', fontSize: '13.5px', lineHeight: 1.5, fontWeight: 600, color: L.muted }}>
					{petNames && `${petNames} · `}{whenShort}.
					{confirm === 'noshow' && canChargeFee && ' The configured no-show fee (if any) will be charged.'}
					{confirm === 'cancel' && withinCancelWindow && ' This is within your late-cancellation window — you can charge the configured fee.'}
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mt: '20px' }}>
					{confirm === 'noshow' && (
						<MbPrimaryButton
							bg={WARN_IC}
							activeBg="#A87423"
							onClick={() => { setConfirm(null); onNoShow() }}
							sx={{ flex: '1 1 100%', width: '100%' }}
						>
							Mark no-show
						</MbPrimaryButton>
					)}
					{confirm === 'cancel' && withinCancelWindow && (
						<MbPrimaryButton
							bg={DANGER}
							activeBg="#A53B36"
							onClick={() => { setConfirm(null); onCancelWithFee() }}
							sx={{ flex: '1 1 100%', width: '100%' }}
						>
							Cancel with fee
						</MbPrimaryButton>
					)}
					{confirm === 'cancel' && (
						<MbPrimaryButton
							bg={withinCancelWindow ? '#6E6889' : DANGER}
							activeBg={withinCancelWindow ? '#5B5573' : '#A53B36'}
							onClick={() => { setConfirm(null); onCancelNoFee() }}
							sx={{ flex: '1 1 100%', width: '100%' }}
						>
							{withinCancelWindow ? 'Cancel without fee' : 'Cancel appointment'}
						</MbPrimaryButton>
					)}
					<Box
						component="button"
						type="button"
						onClick={() => setConfirm(null)}
						sx={{
							width: '100%', height: 52, borderRadius: '14px',
							border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF', color: L.muted,
							fontSize: '15px', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
							'&:active': { color: L.text, borderColor: L.muted },
						}}
					>
						Keep appointment
					</Box>
				</Box>
			</MobileSheet>
		</Box>
	)
}

const sheetItemSx = {
	display: 'flex', alignItems: 'center', gap: '13px', width: '100%',
	border: `1px solid ${L.border}`, bgcolor: L.field, borderRadius: '15px',
	p: '13px 14px', mb: '10px', fontFamily: 'inherit', cursor: 'pointer',
	'&:active': { bgcolor: L.surface },
}

const sheetIcSx = {
	width: 42, height: 42, flex: 'none', borderRadius: '12px',
	display: 'flex', alignItems: 'center', justifyContent: 'center',
}

/* invoice sheet rows (design .mbapd-sheet-item-1 / .mbapd-sheet-ic.inv) */
const invSheetItemSx = {
	...sheetItemSx,
	p: '11px 14px',
	'&:disabled': { opacity: 0.6, cursor: 'default' },
}

const invSheetIcSx = {
	...sheetIcSx,
	width: 38, height: 38, borderRadius: '11px',
	bgcolor: L.primarySoft, color: L.primaryDeep,
}

const invSheetTxtSx = { flex: 1, minWidth: 0, textAlign: 'left', fontSize: '15px', fontWeight: 750, color: L.text }
